import React, { useEffect, useMemo, useRef, useState } from 'react';
import BusinessCreditSummary from '@/components/Business/BusinessCreditSummary';
import { AppState, Platform, Pressable, StyleSheet, View } from 'react-native';
import { useAuthStore, useModalStore } from '@/store';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import { PLUS_MONTHLY_CREDITS_DISPLAY, PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY } from '@/constants/config';
import { formatCreditBalance } from '@/lib/formatCredits';
import { formatCreditResetDate, getPlusCreditOptions } from '@/lib/plusCreditOptions';
import { workCreditRecoveryState } from '@/lib/workCredits';
import { availableCredits } from '@/lib/availableCredits';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';

export function WorkCreditReset({ user }) {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(isDarkTheme(theme)), [theme]);
  const options = getPlusCreditOptions(user);
  if (!options.isPlus) return null;
  const date = formatCreditResetDate(options.resetDate);
  return <View style={styles.reset}>
    <Icon name="calendar" size={13} color={styles.secondary.color} />
    <Text size="xs" style={[styles.secondary, styles.resetText]}>
      {date ? `${formatCreditBalance(options.monthlyTotal)} ${options.isPlusPremium ? 'Plus Premium' : 'Plus'} credits reset on ${date}.`
        : `Your plan includes ${formatCreditBalance(options.monthlyTotal)} credits each month. The next reset date is temporarily unavailable.`}
    </Text>
  </View>;
}

export default function WorkCreditRecoveryCard({ error, requiredCredits, onResume, isResuming = false, mode = 'paused', onClose }) {
  const { theme } = useTheme();
  const user = useAuthStore(s => s.user);
  const { openModal } = useModalStore(s => s.actions);
  const initialBalance = useRef(availableCredits(user?.credits) ?? 0);
  const [checking, setChecking] = useState(false);
  const [notice, setNotice] = useState('');
  const [balanceChecked, setBalanceChecked] = useState(false);
  const { available, required, ready, reserved: held, shortfall } = workCreditRecoveryState({ error, requiredCredits, balance: availableCredits(user?.credits), initialBalance: initialBalance.current, balanceChecked });
  const options = getPlusCreditOptions(user);
  const dark = isDarkTheme(theme);
  const styles = useMemo(() => createStyles(dark), [dark]);

  // Purchases can complete in another tab or a native store sheet. Refresh on
  // return, and also offer an explicit check when the provider is still settling.
  useEffect(() => {
    const refresh = () => { void useAuthStore.getState().fetchUserCredits({ force: true }); };
    if (Platform.OS === 'web') {
      window.addEventListener('focus', refresh);
      return () => window.removeEventListener('focus', refresh);
    }
    const sub = AppState.addEventListener('change', state => { if (state === 'active') refresh(); });
    return () => sub.remove();
  }, []);

  const checkBalance = async () => {
    if (checking) return;
    setChecking(true);
    const result = await useAuthStore.getState().fetchUserCredits({ force: true });
    setBalanceChecked(!!result);
    setNotice(result ? held ? 'Balance refreshed. After adding credits or when your other tasks finish, try again.' : 'Balance refreshed. If you just paid, your credits may take a moment to arrive.' : 'Couldn’t refresh your balance. Please try again.');
    setChecking(false);
  };
  const buy = () => {
    onClose?.();
    openModal('CreditsPurchaseModal', { initialTab: 'buy-credits', workContext: true, requiredCredits: required, returnToWork: true });
  };
  const upgrade = () => {
    onClose?.();
    openModal('PricingModal', { initialCadence: 'monthly' });
  };
  const title = held ? 'You’re running low on available credits' : ready ? 'You’re ready to keep going' : mode === 'start' ? 'Add credits to bring your idea to life'
    : mode === 'step' ? 'Add credits for the next step'
    : 'Add credits to continue';

  if (user?.credits?.business) return <View testID="work-credit-recovery">
    <BusinessCreditSummary credits={user.credits} onClose={onClose} />
    {ready && onResume && <Button text="Resume work" onPress={onResume} isDisabled={isResuming} isLoading={isResuming} />}
    <Button text="Refresh balance" variant="secondary" onPress={checkBalance} isDisabled={checking} />
  </View>;

  return <View testID="work-credit-recovery" style={styles.card}>
    <View style={styles.heading}>
      <View style={styles.status}>
        <Icon name={ready ? 'check' : mode === 'start' ? 'coins' : 'pause'} size={12} color={styles.secondary.color} />
        <Text size="xs" weight="medium" style={styles.secondary}>{ready ? 'Ready when you are' : held ? 'Credits in use · Progress saved' : mode === 'start' ? 'Ready to start' : 'Work paused · Progress saved'}</Text>
      </View>
      <Text size="xl" weight="bold" accessibilityRole="header" style={styles.title}>{title}</Text>
    </View>
    <Text size="n" style={[styles.secondary, styles.body]}>
      {ready ? 'Your balance has updated. Continue when you’re ready.'
        : held ? 'Your other running tasks have reserved some of your credits for their work, leaving too few available for this step. Your progress is saved. Buy more credits to keep going, or try again when those tasks finish.'
        : mode === 'start' ? 'Your request is still in the composer. Top up now, then send it when you’re ready.'
          : mode === 'step' ? 'Your work is saved. Add credits, then choose how you’d like to continue below.'
          : 'Your files and conversation are saved. Add credits to pick up where you left off.'}
    </Text>
    <View style={styles.creditDetails}>
      <View style={styles.balance}>
        <View style={styles.metric}>
          <Text size="l" weight="semibold" style={styles.primary}>{formatCreditBalance(available)}</Text>
          <Text size="xs" style={styles.secondary}>{held ? 'Credits in your balance' : 'Credits available'}</Text>
        </View>
        {!!shortfall && <View style={styles.metric}>
          <Text size="l" weight="semibold" style={styles.primary}>{formatCreditBalance(shortfall)}</Text>
          <Text size="xs" style={styles.secondary}>More needed for the next step</Text>
        </View>}
      </View>
      {!!shortfall && <Text size="xs" style={[styles.secondary, styles.note]}>The next step needs up to {formatCreditBalance(required)} credits. You’re only charged for what it uses.</Text>}
    </View>
    <View style={styles.actions}>
      {ready && onResume ? <Button text={isResuming ? 'Resuming…' : 'Resume work'} prependIcon="play" size="medium" variant="contrast" style={styles.button} onPress={onResume} isLoading={isResuming} isDisabled={isResuming} accessibilityLabel="Resume work" />
        : ready && onClose ? <Button text={mode === 'start' ? 'Back to my request' : 'Back to Work'} size="medium" variant="contrast" style={styles.button} onPress={onClose} />
          : <Button text="Buy credits" size="medium" variant="contrast" style={styles.button} onPress={buy} accessibilityLabel="Buy credits to continue Work" />}
      {held && (onResume || onClose) && <Button text={isResuming ? 'Resuming…' : 'Try again'} size="medium" variant="secondary" style={[styles.button, styles.secondaryButton]} textStyle={styles.primary} onPress={onResume || onClose} isLoading={isResuming} isDisabled={isResuming} accessibilityLabel="Try Work again" />}
      {!ready && !held && balanceChecked && available > 0 && (onResume || onClose) && <Button text="Try again" size="medium" variant="secondary" style={[styles.button, styles.secondaryButton]} textStyle={styles.primary} onPress={onResume || onClose} isDisabled={isResuming} />}
      {!ready && !held && (options.canUpgradeToPremium || !options.isPlus) && <Button text={options.isPlus ? 'Explore Plus Premium' : 'Explore Plus'} variant="secondary" size="medium" style={[styles.button, styles.secondaryButton]} textStyle={styles.primary} onPress={upgrade} />}
      {!ready && <Pressable accessibilityRole="button" accessibilityLabel="Refresh credit balance" disabled={checking} onPress={checkBalance} style={({ hovered, pressed }) => [styles.check, (hovered || pressed) && styles.checkActive]}><Text size="xs" style={[styles.secondary, styles.link]}>{checking ? 'Checking…' : held ? 'Refresh balance' : 'Already added credits?'}</Text></Pressable>}
    </View>
    {!ready && !held && options.canUpgradeToPremium && <Text size="xs" style={[styles.secondary, styles.note]}>More room for bigger ideas: {PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY} credits every month with Plus Premium.</Text>}
    {!ready && !held && !options.isPlus && <Text size="xs" style={[styles.secondary, styles.note]}>Plus includes Work and {PLUS_MONTHLY_CREDITS_DISPLAY} credits every month.</Text>}
    <WorkCreditReset user={user} />
    {!!notice && !ready && <View accessibilityLiveRegion="polite"><Text size="xs" style={[styles.secondary, styles.note]}>{notice}</Text></View>}
  </View>;
}

const createStyles = dark => StyleSheet.create({
  card: { borderWidth: 1, borderRadius: 16, padding: 24, gap: 16, marginBottom: 12, backgroundColor: dark ? '#1C1C1C' : '#FFFFFF', borderColor: dark ? '#383838' : '#E5E5E5' },
  primary: { color: dark ? '#FFFFFF' : '#040404', fontVariant: ['tabular-nums'] },
  secondary: { color: dark ? '#B3B3B3' : '#616161' },
  heading: { gap: 8 },
  status: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  title: { color: dark ? '#FFFFFF' : '#040404', lineHeight: 28, letterSpacing: -0.4 },
  body: { lineHeight: 22 },
  creditDetails: { gap: 12, borderTopWidth: 1, borderColor: dark ? '#383838' : '#E5E5E5', paddingTop: 16 },
  balance: { flexDirection: 'row', flexWrap: 'wrap', gap: 16 },
  metric: { flexGrow: 1, flexBasis: 160, gap: 4 },
  note: { lineHeight: 18 },
  actions: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', columnGap: 16, rowGap: 8 },
  button: { minHeight: 44, borderRadius: 10, paddingHorizontal: 16 },
  secondaryButton: { backgroundColor: 'transparent', borderColor: '#808080' },
  check: { minHeight: 44, justifyContent: 'center', borderRadius: 8, paddingHorizontal: 4 },
  checkActive: { backgroundColor: dark ? '#383838' : '#F5F5F5' },
  link: { textDecorationLine: 'underline' },
  reset: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  resetText: { flex: 1, lineHeight: 18 },
});
